import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useBaseStore } from '@haskoning/vue-basestore'

import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'
import type {
  HardwareProfile,
  HardwareProfileFormPayload,
  HardwareProfileOptions,
  HardwareType
} from '@/stores/type/calculation.type'

const { api } = useApi()
const baseUrl = API.CORE.HARDWARE_PROFILES.INDEX
const hardwareOptionsUrl = API.CORE.HARDWARE_PROFILES.HARDWARE_OPTIONS

export const useHardwareProfileStore = defineStore('hardwareProfile', () => {
  const baseStore = useBaseStore<HardwareProfile>({ service: baseUrl, api })
  const availableItems = ref<HardwareProfile[]>([])
  const availableLoading = ref(false)
  const hardwareOptions = ref<HardwareProfileOptions | null>(null)
  const hardwareOptionsLoading = ref(false)

  const fetchAvailable = async (projectId: number, hardwareType?: HardwareType) => {
    availableLoading.value = true
    try {
      const response = await api.service(baseUrl).find({
        query: {
          available_for_project: projectId,
          ...(hardwareType ? { hardware_type: hardwareType } : {})
        }
      })
      availableItems.value = response?.data ?? []
      return availableItems.value
    } catch (e) {
      console.error(e)
      availableItems.value = []
      return []
    } finally {
      availableLoading.value = false
    }
  }

  const fetchHardwareOptions = async () => {
    hardwareOptionsLoading.value = true
    try {
      hardwareOptions.value = await api.service(hardwareOptionsUrl).get()
      return hardwareOptions.value
    } catch (e) {
      console.error(e)
      hardwareOptions.value = null
      return null
    } finally {
      hardwareOptionsLoading.value = false
    }
  }

  const fetchGlobal = async () => {
    return baseStore.fetchItems({ scope: 'global' })
  }

  const fetchProject = async (projectId: number) => {
    return baseStore.fetchItems({ scope: 'project', project: projectId })
  }

  const createNewHardwareProfile = (project: number | null = null): HardwareProfile => ({
    id: 0,
    name: '',
    description: '',
    project,
    project_name: null,
    hardware_type: 'VIRTUAL_MACHINE',
    hardware_type_display: 'Azure Virtual Machine',
    spot_instance: false,
    vm_size: 'Standard_F8ams_v7',
    vm_image: 'UBUNTU_2404_LTS_SERVER',
    vm_os_disk_size_gb: 32,
    vm_os_disk_storage_account_type: 'StandardSSD_LRS',
    deployment_region: 'westeurope',
    cpu_cores: 8,
    memory_gb: 64
  })

  const saveHardwareProfile = async (profile: HardwareProfileFormPayload) => {
    const payload = {
      ...profile,
      spot_instance: Boolean(profile.spot_instance),
      vm_size: profile.vm_size ?? '',
      vm_image: profile.vm_image ?? 'UBUNTU_2404_LTS_SERVER',
      vm_os_disk_size_gb: Number(profile.vm_os_disk_size_gb ?? 32),
      vm_os_disk_storage_account_type:
        profile.vm_os_disk_storage_account_type || 'StandardSSD_LRS',
      deployment_region: profile.deployment_region || 'westeurope',
      cpu_cores: Number(profile.cpu_cores),
      memory_gb: Number(profile.memory_gb)
    }

    if (profile.id) {
      return baseStore.update(payload as HardwareProfile)
    }
    return baseStore.create(payload)
  }

  const resetAll = () => {
    baseStore.reset()
    availableItems.value = []
    availableLoading.value = false
    hardwareOptions.value = null
    hardwareOptionsLoading.value = false
  }

  return {
    ...baseStore,
    availableItems,
    availableLoading,
    hardwareOptions,
    hardwareOptionsLoading,
    fetchAvailable,
    fetchHardwareOptions,
    fetchGlobal,
    fetchProject,
    createNewHardwareProfile,
    saveHardwareProfile,
    resetAll
  }
})
